import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';

interface ConversationInteractionTagProps {
  interactionType?: string | null;
  feedItemId?: string | null;
  postId?: string | null;
  replyId?: string | null;
  metadata?: Record<string, any>;
  className?: string;
}

function normalizeOptionalText(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : undefined;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  return undefined;
}

function normalizeInteractionType(value: unknown): 'post' | 'reply' | undefined {
  const normalized = normalizeOptionalText(value)?.toLowerCase();
  if (normalized === 'post' || normalized === 'reply') {
    return normalized;
  }
  return undefined;
}

export function ConversationInteractionTag({
  interactionType,
  feedItemId,
  postId,
  replyId,
  metadata,
  className,
}: ConversationInteractionTagProps) {
  const normalizedInteractionType =
    normalizeInteractionType(interactionType) ?? normalizeInteractionType(metadata?.interaction_type);
  if (!normalizedInteractionType) {
    return null;
  }

  const resolvedFeedItemId = normalizeOptionalText(feedItemId) ?? normalizeOptionalText(metadata?.feed_item_id);
  const resolvedPostId = normalizeOptionalText(postId) ?? normalizeOptionalText(metadata?.post_id);
  const resolvedReplyId = normalizeOptionalText(replyId) ?? normalizeOptionalText(metadata?.reply_id);
  const label = normalizedInteractionType === 'post' ? 'Post' : 'Post reply';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={`inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-800 normal-case dark:bg-violet-500/30 dark:text-violet-100 ${className ?? ''}`}
        >
          {label}
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="max-w-none border border-black/10 bg-white p-0 text-inherit shadow-xl dark:border-white/10 dark:bg-neutral-900"
      >
        <div className="w-[min(320px,75vw)] rounded-2xl p-3 text-xs text-slate-700 dark:text-neutral-200">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 dark:text-neutral-400">
            Interaction context
          </p>
          <dl className="mt-2 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-[11px] normal-case">
            <dt className="text-slate-500 dark:text-neutral-400">feed_item_id</dt>
            <dd className="font-mono text-[10px] text-slate-900 dark:text-neutral-100">{resolvedFeedItemId || '-'}</dd>
            <dt className="text-slate-500 dark:text-neutral-400">post_id</dt>
            <dd className="font-mono text-[10px] text-slate-900 dark:text-neutral-100">{resolvedPostId || '-'}</dd>
            <dt className="text-slate-500 dark:text-neutral-400">reply_id</dt>
            <dd className="font-mono text-[10px] text-slate-900 dark:text-neutral-100">{resolvedReplyId || '-'}</dd>
          </dl>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

